import React, { useState } from 'react';
import { HelpCircle, MessageSquarePlus, Camera, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';

export function HelpButton({ collapsed, setMobileOpen }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (screenshots.length + files.length > 3) {
      toast.error('Maximum 3 screenshots allowed');
      return;
    }
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const tempId = `temp_${Date.now()}`;
        setScreenshots(prev => [...prev, { id: tempId, filename: file.name, preview: base64, uploading: true }]);
        try {
          setUploading(true);
          const { data } = await api.post('/support/screenshot', {
            image: base64,
            filename: file.name,
          });
          setScreenshots(prev =>
            prev.map(s => s.id === tempId
              ? { ...s, id: data.attachment_id, uploading: false }
              : s
            )
          );
        } catch (err) {
          toast.error('Failed to upload screenshot');
          setScreenshots(prev => prev.filter(s => s.id !== tempId));
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const removeScreenshot = (id) => {
    setScreenshots(prev => prev.filter(s => s.id !== id));
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error('Please fill in both subject and description');
      return;
    }
    if (screenshots.some(s => s.uploading)) {
      toast.error('Please wait for screenshots to finish uploading');
      return;
    }
    setSubmitting(true);
    try {
      const screenshotRefs = screenshots.map(s => ({
        attachment_id: s.id,
        filename: s.filename,
      }));
      await api.post('/support/ticket', { subject, description, category, priority, screenshots: screenshotRefs });
      toast.success('Support ticket submitted \u2014 our team will review it');
      setOpen(false);
      setSubject('');
      setDescription('');
      setCategory('general');
      setPriority('medium');
      setScreenshots([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit ticket');
    }
    setSubmitting(false);
  };

  return (
    <>
      <Button variant="ghost" size="sm"
        className={`w-full ${collapsed ? 'px-0 justify-center' : 'justify-start gap-2'}`}
        onClick={() => { setOpen(true); setMobileOpen(false); }} data-testid="help-report-btn">
        <HelpCircle className="w-4 h-4" />
        {!collapsed && <span className="text-xs">Help / Report Issue</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5" /> Report an Issue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="help-category-select">
                <option value="general">General</option>
                <option value="bug">Bug / Glitch</option>
                <option value="access">Access / Permission Issue</option>
                <option value="data">Data Problem</option>
                <option value="feature">Feature Request</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="help-priority-select">
                <option value="low">Low \u2014 Feedback / suggestion</option>
                <option value="medium">Medium \u2014 Something isn't working right</option>
                <option value="critical">Critical \u2014 System broken, can't work</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject</label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief summary of the issue"
                className="h-9" data-testid="help-subject-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Describe what happened, what you expected, and any steps to reproduce..."
                className="min-h-[100px] resize-none" data-testid="help-description-input" />
            </div>
            {/* Screenshot Upload */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Screenshots <span className="text-muted-foreground/60">(optional, max 3)</span>
              </label>
              {screenshots.length > 0 && (
                <div className="flex gap-2 mb-2 flex-wrap">
                  {screenshots.map(s => (
                    <div key={s.id} className="relative group w-20 h-20 rounded-md border overflow-hidden bg-muted" data-testid={`screenshot-preview-${s.id}`}>
                      <img src={s.preview} alt={s.filename} className="w-full h-full object-cover" />
                      {s.uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                        </div>
                      )}
                      <button
                        onClick={() => removeScreenshot(s.id)}
                        className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-screenshot-${s.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {screenshots.length < 3 && (
                <label
                  className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-muted-foreground/30 hover:border-primary/50 hover:bg-accent/50 cursor-pointer transition-colors"
                  data-testid="help-screenshot-upload"
                >
                  <Camera className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {uploading ? 'Uploading...' : 'Add screenshot'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                </label>
              )}
              <p className="text-[10px] text-muted-foreground/60 mt-1.5 leading-relaxed">
                Take a screenshot: <span className="font-medium">Mobile</span> \u2014 Power + Volume Down &middot; <span className="font-medium">Windows</span> \u2014 PrtScn &middot; <span className="font-medium">Mac</span> \u2014 Cmd + Shift + 4. Then upload it here.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting || uploading} data-testid="help-submit-btn">
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
